import { Apartment } from "@boark/shared/types/apartment";
import { OrganizationWithHoldingsAndLeasesAndSubletsAndApartment } from "@boark/shared/types/organization-with-holdings-and-leases-and-sublets-and-apartment";
import { getResidencyWithinDate } from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { ApartmentLink } from "../../components/apartment-link";
import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { Tooltip } from "../../components/tooltip";
import { useGetOrganizations } from "../../queries/organization";
import { formatOrganizationLabel } from "../../services/organization";
import { useLocale } from "../../stores/locale";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { recordIdToId } from "../../utils/record-id-to-id";

export type OrganizationWithApartmentData =
  OrganizationWithHoldingsAndLeasesAndSubletsAndApartment & {
    status: "owner" | "tenant" | "subtenant" | "none";
    currentApartment?: Omit<Apartment, "holdings" | "leases">;
  };

export function Organizations() {
  const { locale: t, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: organizations, isLoading: isOrganizationsLoading } =
    useGetOrganizations();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.organization.organizations}</H1>
          <Button route="/admin/register/organization/create">
            {t.organization.create}
          </Button>
        </div>
        <Table
          actions={(row: OrganizationWithApartmentData) => [
            {
              label: t.issue.create,
              route: createIssueRouteForRecord({
                type: "organization",
                recordId: recordIdToId(row.id),
                label: formatOrganizationLabel(row),
              }),
            },
          ]}
          data={(organizations ?? []).map(getOrganizationData)}
          loading={isOrganizationsLoading}
          editRouteButton={(row) =>
            `/admin/register/organization/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/organization/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "apartment.index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="name">{t.organization.name}</Column>
          <Column field="status">{t.organization.status.status}</Column>
          <Column
            body={(row: OrganizationWithApartmentData) =>
              row.currentApartment ? (
                <ApartmentLink apartment={row.currentApartment} />
              ) : (
                ""
              )
            }
            field="currentApartment.index"
          >
            {getLocale("organization.currentApartment")}
          </Column>
          <Column field="corporateRegistrationNumber">
            <Tooltip content={t.organization.corporateRegistrationNumber}>
              {t.organization.corporateRegistrationNumberAbbreviation}
            </Tooltip>
          </Column>
          <Column field="email">{t.organization.email}</Column>
          <Column field="phone">{t.organization.phone}</Column>
          <Column field="issueCount">{t.organization.issueCount}</Column>
        </Table>
      </div>
    </>
  );
}

function getOrganizationData(
  organization: OrganizationWithHoldingsAndLeasesAndSubletsAndApartment,
): OrganizationWithApartmentData {
  let currentApartment:
    | OrganizationWithApartmentData["currentApartment"]
    | undefined = undefined;
  let status: OrganizationWithApartmentData["status"] = "none";

  const currentHolding = getResidencyWithinDate(
    organization.holdings,
    new Date(),
  );
  const currentLease = getResidencyWithinDate(organization.leases, new Date());
  const currentSublet = getResidencyWithinDate(
    organization?.sublets,
    new Date(),
  );

  if (currentHolding) {
    currentApartment = currentHolding.apartment;
    status = "owner";
  } else if (currentLease) {
    currentApartment = currentLease.apartment;
    status = "tenant";
  } else if (currentSublet) {
    currentApartment =
      "holding" in currentSublet
        ? currentSublet.holding.apartment
        : currentSublet.lease.apartment;
    status = "subtenant";
  }

  return {
    ...organization,
    currentApartment,
    status,
  };
}
